import React, { ReactElement, useContext, useEffect } from "react";
import {
	ClientUserContactType,
	CommonServerStatusEnum,
	ListPagePostRequest,
	TableQueryFilter,
	TableQueryFilterOperands,
	VendorOpportunity,
	ZomentumEntities,
	ZomentumRoutes
} from "@/Contracts";
import ZDrawer from "@molecules/ZDrawer";
import {
	Opportunity,
	OpportunityRequest,
	OpportunityType
} from "@/Contracts/Opportunity";
import {
	Client,
	ClientContactBulkUpdate,
	ClientContactBulkUpdateRequest,
	ClientsRequest
} from "@/Contracts/Clients";
import { ChildEntityHydrationParams } from "@/Contracts/Utils";

import { getTaskContact } from "../../../../utils";
import {
	ClientRequestValues,
	ComponentType,
	FormUIPropsType,
	IAddSalesActivityWrapperProps,
	OpportunityRequestValues
} from "../interface";
import useDrawersCommonFunctions from "../../../../hooks/SalesActivity/useSalesActivityDrawersCommonFunctions";
import { ESalesActivityType } from "@/Contracts/Others/SalesActivity";
import {
	IOnlyCommentUIProps,
	IOnlyLogCallUIProps,
	IOnlyMeetingUIProps,
	IOnlyTaskUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import { MissingDetailsModal } from "@organisms/ZMissingDetailsModal";
import ZMessage from "@molecules/ZMessage";
import {
	SalesActivityModuleState,
	useSalesActivityModuleSelector
} from "../../../../store";
import useAccessLevel from "../../../../hooks/useAccessLevel";
import useDatePattern from "@/Hooks/useDatePattern";
import { ParentContext } from "../../../../store/context/ParentContext";
import { isVendorApp } from "@/Utils";
import { useRouteMatch } from "react-router-dom";
import { getAppPathname } from "@/Utils/routing/getAppPathname";

const SalesActivityWapper = <
	T1 extends ComponentType,
	T2 extends FormUIPropsType
>({
	visible = false,
	onClose,
	Component,
	form,
	formUIProps,
	salesActivityType
}: IAddSalesActivityWrapperProps<T1, T2>): ReactElement => {
	const parentContext = useContext(ParentContext);
	const { isLoading } = useSalesActivityModuleSelector(
		(state: SalesActivityModuleState) => {
			return {
				isLoading: state.salesActivity.isLoading
			};
		}
	);
	const { isLoading: isUserLoading } = parentContext.user;
	const { currentActiveOpportunity, addOpportunityDrawerOpen } =
		parentContext.opportunity;
	const { currentActiveClient, addClientDrawerOpen } = parentContext.client;
	const { currentActivePartner } = parentContext.vendorPartner;
	const { currentActiveVendorClient } = parentContext.vendorClient;

	const datePattern = useDatePattern();

	const uiProps =
		salesActivityType === ESalesActivityType.MEETING
			? (formUIProps as IOnlyMeetingUIProps)
			: null;

	const isClientInOpportunityAssigned = useAccessLevel(
		(currentActiveOpportunity as Opportunity)?.client_company_owner_id
	);

	const [clientList, setClientList] = React.useState<Client[]>([]);

	const {
		setOpportunitySearchResult,
		setClientSearchResult,
		setSelectedOpportunity,
		setSelectedClient,
		setClientContactsSearchResult,
		setPartnerSearchResult,
		setVendorOpportunitySearchResult,
		setVendorClientSearchResult,
		setSelectedPartner,
		setSelectedVendorOpportunity,
		setSelectedVendorClient,
		setTaskContact,
		taskContact,
		updateTaskContact,
		clientContactsSearchResult,
		emailThreadMessageDrawerOpen,
		getEmailThreadMessage,
		showEmailThreadMessageDrawer,
		closeEmailThreadMessageDrawer,
		closeAddOpportunityDrawer,
		closeAddClientDrawer,
		fetchOpportunities,
		fetchClients,
		fetchClientContacts,
		ZUserMultiSelectFormItem,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem,
		ZVendorPartnerSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZVendorClientSelectFormItem,
		ZUserSelectFormItem,
		selectedClient
	} = useDrawersCommonFunctions(
		form,
		salesActivityType,
		[
			ESalesActivityType.MEETING,
			ESalesActivityType.TASK,
			ESalesActivityType.LOGCALL,
			ESalesActivityType.COMMENT
		].includes(salesActivityType)
			? (
					formUIProps as IOnlyMeetingUIProps &
						IOnlyTaskUIProps &
						IOnlyLogCallUIProps &
						IOnlyCommentUIProps
			  ).handleOwnerChange
			: null,
		clientList
	);

	const opportunityFilter: TableQueryFilter[] = [
		new TableQueryFilter(
			TableQueryFilterOperands.MongoDbEqualTo,
			"client_company_id",
			currentActiveClient?.id,
			null,
			false
		)
	];

	const atOpportunityDetailsPage = (): boolean => {
		return (
			!!currentActiveOpportunity &&
			(getAppPathname() ===
				ZomentumRoutes.OpportunityDetail.replace(
					":id",
					currentActiveOpportunity.id
				) ||
				getAppPathname() === ZomentumRoutes.OpportunityList)
		);
	};

	const atClientDetailPage = (): boolean => {
		return (
			!!currentActiveClient &&
			(getAppPathname() ===
				ZomentumRoutes.ClientsDetail.replace(
					":id",
					currentActiveClient.id
				) ||
				getAppPathname() === ZomentumRoutes.ClientsList)
		);
	};

	const createGetClientsRequest = (
		clientId: string | undefined
	): ClientsRequest => {
		return new ClientsRequest(
			new Array<TableQueryFilter>(),
			null,
			ClientRequestValues.PAGE_SIZE,
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.CLIENT_USERS_COUNT
			),
			undefined,
			clientId
		);
	};

	const createGetOpportunitiesRequest = (
		opportunityId: string | undefined
	): OpportunityRequest => {
		return new OpportunityRequest(
			opportunityFilter,
			null,
			[],
			OpportunityRequestValues.PAGE_SIZE,
			opportunityId
		);
	};

	const atPartnerDetailPage = (): boolean => {
		return (
			!!currentActivePartner &&
			(getAppPathname() ===
				ZomentumRoutes.VendorPartnersDetail.replace(
					":id",
					currentActivePartner.id
				) ||
				getAppPathname() === ZomentumRoutes.VendorPartnerList)
		);
	};

	const atVendorClientDetailPage = (): boolean => {
		return (
			!!currentActiveVendorClient &&
			(getAppPathname() ===
				ZomentumRoutes.VendorClientDetail.replace(
					":id",
					currentActiveVendorClient.id
				) ||
				getAppPathname() === ZomentumRoutes.VendorClientList)
		);
	};

	const fetchDataForPartnerApp = async () => {
		if (visible) {
			const currentActiveOpportunity = parentContext.opportunity
				.currentActiveOpportunity as Opportunity;
			const isOpportunityDetailsPage = atOpportunityDetailsPage();

			const isClientSelected = atClientDetailPage();

			const entityType = isOpportunityDetailsPage
				? ZomentumEntities.Opportunity
				: isClientSelected
				? ZomentumEntities.Client
				: ZomentumEntities.Opportunity;

			form.setFieldsValue({
				relation: entityType
			});

			let initialOpportunityId: string | null = null;
			let initialClientId: string | null = null;
			const opportunityId = currentActiveOpportunity?.id;
			const clientId =
				currentActiveOpportunity?.client_company_id ??
				currentActiveClient?.id;

			const opportunities = await fetchOpportunities(
				createGetOpportunitiesRequest(opportunityId)
			);
			setOpportunitySearchResult(opportunities);

			const clients = await fetchClients(
				createGetClientsRequest(clientId)
			);
			setClientSearchResult(clients);

			if (
				currentActiveOpportunity &&
				entityType === ZomentumEntities.Opportunity &&
				isOpportunityDetailsPage
			) {
				initialOpportunityId = currentActiveOpportunity.id ?? null;
				initialClientId = !currentActiveOpportunity.is_onboarding_entity
					? currentActiveOpportunity.client_company_id ?? null
					: null;
				const clientListForAssignedAccess =
					!isClientInOpportunityAssigned &&
					!currentActiveOpportunity.is_onboarding_entity
						? [
								{
									name: currentActiveOpportunity.client_company_name,
									id: currentActiveOpportunity.client_company_id
								} as Client
						  ]
						: [];
				setClientList(clientListForAssignedAccess);
			} else if (
				currentActiveClient &&
				entityType === ZomentumEntities.Client &&
				isClientSelected
			) {
				initialClientId = currentActiveClient.id ?? null;
			}
			form.setFieldsValue({
				[`${ZomentumEntities.Opportunity}`]: initialOpportunityId,
				[`${ZomentumEntities.Client}`]: initialClientId
			});

			if (
				initialClientId &&
				(salesActivityType === ESalesActivityType.TASK ||
					salesActivityType === ESalesActivityType.MEETING)
			) {
				const contacts = await fetchClientContacts(initialClientId);
				setTaskContact(
					getTaskContact(
						entityType,
						entityType === ZomentumEntities.Opportunity
							? initialOpportunityId
							: initialClientId,
						contacts,
						opportunities,
						clients
					)
				);
				setClientContactsSearchResult(contacts);
			}
		} else {
			setClientSearchResult(null);
			setOpportunitySearchResult(null);
			setClientContactsSearchResult(null);
			setTaskContact(null);
		}
	};

	const fetchDataForVendorApp = async () => {
		if (visible) {
			const currentActiveOpportunity = parentContext.opportunity
				.currentActiveOpportunity as VendorOpportunity;
			const isPartnerSelected = atPartnerDetailPage();
			const isClientSelected = atVendorClientDetailPage();
			const isOpportunityDetailsPage = atOpportunityDetailsPage();

			const entityType = isOpportunityDetailsPage
				? ZomentumEntities.VendorOpportunity
				: isPartnerSelected
				? ZomentumEntities.VendorCompany
				: isClientSelected
				? ZomentumEntities.VendorClient
				: ZomentumEntities.VendorOpportunity;
			form.setFieldsValue({
				relation: entityType
			});
			let initialOpportunityId: string | null = null;
			let initialPartnerId: string | null = null;
			let initialClientId: string | null = null;
			const partnerId = currentActivePartner?.id;
			const opportunityId = currentActiveOpportunity?.id;
			const clientId = currentActiveVendorClient?.id;
			const partnerResponse =
				await parentContext.vendorPartner.getPartnerListPagePost(
					new ListPagePostRequest({
						count_query_flag: true,
						filter_criterion: [],
						sort_criterion: [],
						page_no: 1,
						page_size: 50,
						includedChildEntities: [
							ChildEntityHydrationParams.USERS_ALL
						]
					})
				);
			setPartnerSearchResult(partnerResponse.data);
			const clientResponse =
				await parentContext.vendorClient.getClientListPagePost(
					new ListPagePostRequest({
						count_query_flag: true,
						filter_criterion: [],
						sort_criterion: [],
						page_no: 1,
						page_size: 50,
						includedChildEntities: [
							ChildEntityHydrationParams.USERS_ALL
						]
					})
				);
			setVendorClientSearchResult(clientResponse.data);
			const opportunityResponse =
				await parentContext.opportunity.getOpportunityListPost(
					new ListPagePostRequest({
						count_query_flag: true,
						filter_criterion: [],
						sort_criterion: [],
						page_no: 1,
						page_size: 50,
						includedChildEntities: [
							ChildEntityHydrationParams.USERS_ALL
						]
					})
				);
			setVendorOpportunitySearchResult(opportunityResponse.data);
			if (
				currentActivePartner &&
				entityType === ZomentumEntities.VendorCompany &&
				isPartnerSelected
			) {
				initialPartnerId = partnerId ?? null;
			} else if (
				currentActiveVendorClient &&
				entityType === ZomentumEntities.VendorClient &&
				isClientSelected
			) {
				initialClientId = clientId ?? null;
			} else if (
				currentActiveOpportunity &&
				entityType === ZomentumEntities.VendorOpportunity &&
				isOpportunityDetailsPage
			) {
				initialOpportunityId = opportunityId ?? null;
				initialClientId =
					currentActiveOpportunity.vendor_client_company_id ?? null;
			}
			form.setFieldsValue({
				[`${ZomentumEntities.VendorOpportunity}`]: initialOpportunityId,
				[`${ZomentumEntities.VendorCompany}`]: initialPartnerId,
				[`${ZomentumEntities.VendorClient}`]: initialClientId
			});

			if (
				initialClientId &&
				(salesActivityType === ESalesActivityType.TASK ||
					salesActivityType === ESalesActivityType.MEETING)
			) {
				const contacts = await fetchClientContacts(initialClientId);
				setClientContactsSearchResult(contacts);
			}
		} else {
			setPartnerSearchResult(null);
			setVendorClientSearchResult(null);
			setVendorOpportunitySearchResult(null);
			setTaskContact(null);
			setClientContactsSearchResult(null);
		}
	};

	useEffect(() => {
		if (isVendorApp()) {
			fetchDataForVendorApp();
		} else {
			fetchDataForPartnerApp();
		}
	}, [visible]);

	const handleOpportunitiesOnDrawerSubmit = async (
		opportunity: Opportunity
	): Promise<void> => {
		const opportunities = await fetchOpportunities(
			createGetOpportunitiesRequest(opportunity.id)
		);

		setOpportunitySearchResult(opportunities);
		setSelectedOpportunity(opportunity.id);
		form.setFieldsValue({
			[`${ZomentumEntities.Opportunity}`]: opportunity.id
		});
	};

	const handleClientOnDrawerSubmit = async (
		opportunity: Opportunity,
		selectedClientId: string | null = null
	): Promise<void> => {
		let clientId = opportunity?.client_company_id;
		if (selectedClientId) {
			clientId = selectedClientId;
		}
		if (clientId) {
			setSelectedClient(clientId);
			form.setFieldsValue({
				[`${ZomentumEntities.Client}`]: clientId
			});
		}
		const clients = await fetchClients(createGetClientsRequest(clientId));
		setClientSearchResult(clients);
	};

	const opportunityDrawerOnSubmit = async (submitResponse: Opportunity) => {
		handleOpportunitiesOnDrawerSubmit(submitResponse);
		handleClientOnDrawerSubmit(submitResponse);
	};

	const clientDrawerOnSubmit = async (submitResponse: Client) => {
		handleClientOnDrawerSubmit(
			currentActiveOpportunity as Opportunity,
			submitResponse?.id
		);
	};

	const clientContactBulkUpdateSubmitHandler = async (
		values: ClientContactBulkUpdate[]
	) => {
		const payload: ClientContactBulkUpdateRequest = { data: [...values] };
		try {
			const { results } =
				await parentContext.client.bulkUpdateClientContactEmail(
					payload
				);

			results.forEach(value => {
				if (value.status === CommonServerStatusEnum.Failure) {
					ZMessage.error(value.response);
					return;
				}
			});
			const clientId = form.getFieldValue(ZomentumEntities.Client);
			if (!clientId) return;

			const clientContacts = await fetchClientContacts(clientId);
			setClientContactsSearchResult(clientContacts);
			uiProps && uiProps.setAttendeesWithoutEmail([]);
		} catch (err) {
			console.error(err);
		}
		uiProps && uiProps.setShowAddMissingDetailsModal(false);
		form.submit();
	};

	const emailThreadMessage =
		salesActivityType === ESalesActivityType.TASK
			? getEmailThreadMessage()
			: null;

	const openAddClientContactDrawer = () => {
		parentContext.client.addClientContactDrawerOpened();
	};

	const UIProps: unknown = {
		onClose,
		form,
		isLoading,
		taskContact,
		clientContactsSearchResult,
		showEmailThreadMessageDrawer,
		ZUserMultiSelectFormItem,
		updateTaskContact,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem,
		ZUserSelectFormItem,
		isSubmitDisabled: isLoading || isUserLoading,
		datePattern,
		ZVendorPartnerSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZVendorClientSelectFormItem,
		openAddClientContactDrawer,
		setSelectedClient,
		...formUIProps
	};

	const TypedComponent = Component;
	const typedUIProps = UIProps as T1;
	const handleClose = () => {
		setSelectedPartner(null);
		setSelectedVendorOpportunity(null);
		setSelectedVendorClient(null);
		setSelectedOpportunity(null);
		setSelectedClient(null);
		onClose && onClose();
	};

	const currentActiveClientId = isVendorApp()
		? (currentActiveOpportunity as VendorOpportunity)
				?.vendor_client_company_id
		: (currentActiveOpportunity as Opportunity)?.client_company_id ??
		  currentActiveClient?.id;

	const addContactType =
		currentActiveClient?.client_users?.data.length ||
		!!currentActiveOpportunity?.primary_client_user_id?.length
			? ClientUserContactType.SecondaryContact
			: ClientUserContactType.PrimaryContact;

	const isSalesActivityPage = !!useRouteMatch(
		ZomentumRoutes.SalesActivityPage
	);

	const selectedClientContactType = !!clientContactsSearchResult?.length
		? ClientUserContactType.SecondaryContact
		: ClientUserContactType.PrimaryContact;

	return (
		<>
			<ZDrawer
				title={`Add ${salesActivityType}`}
				removeDefaultPadding
				visible={visible}
				closable
				destroyOnClose
				onClose={handleClose}
			>
				<TypedComponent {...typedUIProps} />

				<parentContext.opportunity.AddOpportunityDrawer
					visible={addOpportunityDrawerOpen}
					onClose={closeAddOpportunityDrawer}
					activeClientUserId={null}
					activeOpportunityType={
						OpportunityType.ApplicationDevelopment
					}
					activeClientCompanyId={null}
					onSubmit={opportunityDrawerOnSubmit}
				/>
				<parentContext.client.AddClientDrawer
					visible={addClientDrawerOpen}
					onClose={closeAddClientDrawer}
					onSubmit={clientDrawerOnSubmit}
				/>
				{isVendorApp() ? (
					<parentContext.client.AddClientContactDrawer
						visible={
							parentContext.client.addClientContactDrawerOpen
						}
						currentActiveVendorClientContact={null}
						onClose={
							parentContext.client.addClientContactDrawerClosed
						}
						handleClientContactRefresh={clientContact => {
							setClientContactsSearchResult(contacts => [
								...contacts,
								clientContact
							]);
						}}
						initalVendorClientContact={false}
						selectedClient={
							isSalesActivityPage
								? selectedClient
								: currentActiveClientId
						}
					/>
				) : (
					<parentContext.client.AddClientContactDrawer
						visible={
							parentContext.client.addClientContactDrawerOpen
						}
						clientId={
							isSalesActivityPage
								? selectedClient
								: currentActiveClientId
						}
						activeTabContactType={
							isSalesActivityPage
								? selectedClientContactType
								: addContactType
						}
						onSubmit={clientContact => {
							setClientContactsSearchResult(contacts => [
								...contacts,
								clientContact
							]);
						}}
						onClose={
							parentContext.client.addClientContactDrawerClosed
						}
					/>
				)}
				{isVendorApp() ? (
					<parentContext.emailThread.EmailThreadMessageDrawer
						visible={emailThreadMessageDrawerOpen}
						onClose={closeEmailThreadMessageDrawer}
						isNewThread
						isDocumentMessage={false}
						partnerId={form.getFieldValue(ZomentumEntities.Client)}
						opportunityId={form.getFieldValue(
							ZomentumEntities.Opportunity
						)}
						useTemplate={false}
						isFromDocumentsPage={false}
						defaultRecipients={taskContact?.email}
						emailThreadMessage={emailThreadMessage}
					/>
				) : (
					<parentContext.emailThread.EmailThreadMessageDrawer
						visible={emailThreadMessageDrawerOpen}
						onClose={closeEmailThreadMessageDrawer}
						isNewThread
						isDocumentMessage={false}
						clientId={form.getFieldValue(ZomentumEntities.Client)}
						opportunityId={form.getFieldValue(
							ZomentumEntities.Opportunity
						)}
						documentId={null}
						useTemplate={false}
						isFromDocumentsPage={false}
						defaultRecipients={taskContact?.email}
						emailThreadMessage={emailThreadMessage}
					/>
				)}
				{uiProps && (
					<MissingDetailsModal
						title="Missing Attendees’ Email"
						visible={uiProps.showAddMissingDetailsModal}
						onSubmit={clientContactBulkUpdateSubmitHandler}
						onCancel={() =>
							uiProps.setShowAddMissingDetailsModal(false)
						}
						missingEmailKeys={uiProps.attendeesWithoutEmail}
						subtitle="For assigning Attendees to the meeting, you need to add emails for the following recipients"
					/>
				)}
			</ZDrawer>
		</>
	);
};

export default SalesActivityWapper;
